import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import { Button, Select } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { Trash2 } from 'lucide-react';

import { NumericInputCell } from '../NumericInputCell';

import { AddDeductionButton } from './AddDeductionButton';
import { gridLayoutMaterialReconcilitation } from './constants/gridLayout';

import {
  $addDeduction,
  $materialsReconciliationForm,
  $removeDeduction,
  $updateDeduction,
  ReconciliationDeductionForm,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { DeductionTypeRecord } from '~/modules/trade/types/deductionType';
import { zeroMoney } from '~/modules/trade/utils/money';

interface LoadLevelDeductionsCardProps {
  shippedNet: number;
  shippedTotal: number;
  deductionTypes: DeductionTypeRecord[];
  disabled?: boolean;
}

export const LoadLevelDeductionsCard = ({
  shippedNet,
  shippedTotal,
  deductionTypes,
  disabled = false,
}: LoadLevelDeductionsCardProps) => {
  const t = useTranslations();
  const formState = useAtomValue($materialsReconciliationForm);
  const addDeduction = useSetAtom($addDeduction);
  const updateDeduction = useSetAtom($updateDeduction);
  const removeDeduction = useSetAtom($removeDeduction);

  // Filter load-level deductions
  const loadLevelDeductions = formState.deductions.filter(
    (d) => !d.receivedLineItemId,
  );

  // Calculate average unit price for the load
  const avgUnitPrice = shippedNet > 0 ? shippedTotal / shippedNet : 0;

  const handleAddDeduction = (type: string) => {
    addDeduction({
      receivedLineItemId: undefined,
      deductionTypeId: type,
      weightQuantity: { value: 0, unit: enumUnit.Pounds, __typename: 'Weight' },
      priceQuantity: zeroMoney(),
    });
  };

  const handleWeightChange = (
    deduction: ReconciliationDeductionForm,
    value: number,
  ) => {
    // For weight-based, update both quantity and unitPrice
    const deductionType = deductionTypes.find(
      (dt) => dt.id === deduction.deductionTypeId,
    );

    if (deductionType?.applicationType === 'Weight') {
      updateDeduction(deduction.id, {
        weightQuantity: { value, unit: enumUnit.Pounds, __typename: 'Weight' },
        priceQuantity: {
          ...zeroMoney(),
          amount: value * avgUnitPrice,
        },
      });
    } else {
      updateDeduction(deduction.id, {
        priceQuantity: {
          ...zeroMoney(),
          amount: value,
        },
      });
    }
  };

  const handlePriceChange = (
    deduction: ReconciliationDeductionForm,
    value: number,
  ) => {
    const deductionType = deductionTypes.find(
      (dt) => dt.id === deduction.deductionTypeId,
    );

    if (deductionType?.applicationType === 'Weight') {
      updateDeduction(deduction.id, {
        weightQuantity: { value, unit: enumUnit.Pounds, __typename: 'Weight' },
        priceQuantity: {
          ...zeroMoney(),
          amount: value * avgUnitPrice,
        },
      });
    } else {
      updateDeduction(deduction.id, {
        priceQuantity: {
          ...zeroMoney(),
          amount: value,
        },
      });
    }
  };

  const options = React.useMemo(
    () =>
      deductionTypes?.map((dt) => ({
        key: dt.id,
        label: dt.name,
        mnemonic: '',
      })),
    [deductionTypes],
  );

  // If it's disabled and there are no load-level deductions, return null
  if (disabled && loadLevelDeductions.length === 0) {
    return null;
  }

  return (
    <div className="@LoadLevelDeductionsCard mt-4 bg-white py-4">
      <div className="mb-2 flex items-center justify-between">
        {loadLevelDeductions.length > 0 ? (
          <div className="text-base font-semibold text-failure">
            {t.materialsReconciliation.loadLevelDeductions.title}
          </div>
        ) : (
          <div className="py-4 text-neutral/60">
            {t.materialsReconciliation.loadLevelDeductions.emptyState}
          </div>
        )}
        {!disabled && (
          <AddDeductionButton
            onAddDeduction={handleAddDeduction}
            isDisabled={disabled}
            color="failure"
          >
            {t.materialsReconciliation.loadLevelDeductions.addButton}
          </AddDeductionButton>
        )}
      </div>
      {loadLevelDeductions.length > 0 && (
        <div className="space-y-2">
          {loadLevelDeductions?.map((deduction) => {
            const deductionType = deductionTypes.find(
              (dt) => dt.id === deduction.deductionTypeId,
            );
            const isWeightDeduction =
              deductionType?.applicationType === 'Weight';

            const calculatedPrice = isWeightDeduction
              ? (deduction.weightQuantity?.value || 0) * avgUnitPrice
              : deduction.priceQuantity?.amount || 0;

            return (
              <div
                key={deduction.id}
                className={cn(
                  'grid w-full items-center gap-2',
                  gridLayoutMaterialReconcilitation,
                )}
              >
                {/* Column 1: Deduction Type Selector (editable) */}
                <Select
                  className="text-failure focus-within:border-failure"
                  value={deduction.deductionTypeId}
                  options={options}
                  onChange={(id) =>
                    updateDeduction(deduction.id, { deductionTypeId: id })
                  }
                  isDisabled={disabled}
                />
                {/* Column 2-5: Empty */}
                <div />
                <div />
                <div />
                <div />
                {/* Column 6: Weight Field (if weight deduction) */}
                {isWeightDeduction ? (
                  <NumericInputCell
                    className="text-failure focus-within:border-failure focus-within:ring-failure focus:ring-failure"
                    value={deduction.weightQuantity?.value || 0}
                    onChange={(v) => handleWeightChange(deduction, v)}
                    prefix="W"
                    suffix="lb"
                    isDisabled={disabled}
                  />
                ) : (
                  <div />
                )}
                {/* Column 7: Price Field */}
                <NumericInputCell
                  className={cn(
                    'text-failure focus-within:border-failure focus-within:ring-failure focus:ring-failure',
                    isWeightDeduction && '[&_.group]:!bg-failure/10',
                  )}
                  value={calculatedPrice}
                  onChange={(v) => handlePriceChange(deduction, v)}
                  isDisabled={isWeightDeduction || disabled}
                  prefix="$"
                />
                {/* Column 8-9: Empty */}
                <div />
                <div />
                {/* Column 10: Actions Button */}
                {!disabled && (
                  <Button
                    variant="outline"
                    color="failure"
                    size="icon"
                    icon={<Trash2 size={18} />}
                    className="size-[40px]"
                    onPress={() => removeDeduction(deduction.id)}
                    isDisabled={disabled}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
